'use client';

import { useCallback, useMemo } from 'react';

import type { ResponseDashTriviaInit } from '@/types/dashboard';

interface UseTriviaResultsProps {
  triviaData?: ResponseDashTriviaInit;
  totalStats?: boolean;
  isMobile?: boolean;
}

export const useTriviaResults = ({
  triviaData,
  totalStats,
  isMobile,
}: UseTriviaResultsProps) => {
  const isWinner = useMemo(() => {
    if (!triviaData) return false;
    return (triviaData?.resultadoParticipacion?.cantidadDeMedallas ?? 0) > 0;
  }, [triviaData]);

  const triviaTime = useMemo(() => {
    if (!triviaData) return '-';

    if (totalStats) {
      if ((triviaData?.resultadoRanking?.medallasTotales ?? 0) > 0) {
        return triviaData?.resultadoRanking?.tiempoUtilizadoConFormato ?? '-';
      }
      return '-';
    } else {
      if (!triviaData?.resultadoParticipacion?.huboTimeout) {
        return (
          triviaData?.resultadoParticipacion?.tiempoUtilizadoConFormato ?? '-'
        );
      }
      if (triviaData?.tiemposTriviaActual) {
        const totalTime =
          triviaData?.tiemposTriviaActual?.tiempoTotalDeParticipacion;
        return new Date(totalTime * 1000).toISOString().substring(11, 19);
      } else {
        return (
          triviaData?.resultadoParticipacion?.tiempoUtilizadoConFormato ?? '-'
        );
      }
    }
  }, [totalStats, triviaData]);

  const ResultMessage = useCallback(() => {
    if (!triviaData) return null;
    const spanStyle: React.CSSProperties | undefined = isMobile
      ? {
          display: 'block',
          textAlign: 'center',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          maxWidth: '90%',
          margin: '0 auto',
        }
      : undefined;

    if (!isWinner && !totalStats) {
      return <></>;
    }

    if (isWinner && !totalStats) {
      return (
        <span style={spanStyle}>
          Chequeá tu medallero en la próxima publicación de puntos.
        </span>
      );
    }

    if (triviaData.resultadoRanking?.esteRankingCuentaLaUltimaTriviaJugada) {
      return (
        <span style={spanStyle}>
          Se muestran los resultados de todas las trivias jugadas.
        </span>
      );
    } else {
      return (
        <span style={spanStyle}>
          Se muestran los resultados totales sin contar la última trivia jugada.
        </span>
      );
    }
  }, [isWinner, triviaData, totalStats]);

  const title = useMemo(() => {
    if (totalStats) {
      return isMobile ? 'ESTADÍSTICAS TOTALES' : 'Estadísticas Totales';
    }
    const triviaName = triviaData?.trv?.nombre || triviaData?.trvAnt?.nombre;
    const categoryName =
      triviaData?.trv?.nombreCategoria || triviaData?.trvAnt?.nombreCategoria;
    return `Resultados ${triviaName}: ${categoryName}`;
  }, [totalStats, triviaData, isMobile]);

  return {
    isWinner,
    triviaTime,
    ResultMessage,
    title,
  };
};
